/**
 * KPI-doelen voor rapportage: per Werftools-tenant (moederbedrijf), optioneel per bedrijf,
 * recruiter of organisatie-root. v2-store in localStorage; v1 wordt bij eerste lezen gemigreerd.
 */

export type ReportingKpiTargets = {
  jobsAdded: number;
  shortlistsAdded: number;
  cvsSent: number;
  firstInterviews: number;
  furtherInterviews: number;
  totalInterviews: number;
  offers: number;
  /** Live pipeline: Afgewezen + Afgewezen door opdrachtgever */
  rejectedApplications?: number;
  permPlacements: number;
  /** Sollicitaties via werksite / career (o.a. werkenbijvideoworks, WP-interim) */
  websiteApplications: number;
  newCompanies?: number;
};

export type ReportingKpiScope =
  | { kind: 'default' }
  | { kind: 'client'; id: string }
  | { kind: 'recruiter'; id: string }
  | { kind: 'organization'; id: string };

/** Granularity of the numeric KPI targets (scaled to the dashboard date window). */
export type ReportingKpiTargetBasis = 'day' | 'week' | 'month' | 'year';

const BASIS_DAYS: Record<ReportingKpiTargetBasis, number> = {
  day: 1,
  week: 7,
  month: 30.4375,
  year: 365.25,
};

export const DEFAULT_REPORTING_KPI_TARGETS: ReportingKpiTargets = {
  jobsAdded: 783,
  shortlistsAdded: 480,
  cvsSent: 365,
  firstInterviews: 52,
  furtherInterviews: 52,
  totalInterviews: 157,
  offers: 72,
  rejectedApplications: 0,
  permPlacements: 58,
  websiteApplications: 120,
  newCompanies: 12,
};

const V1_PREFIX = 'werftools_reporting_kpi_v1';
const V2_PREFIX = 'werftools_reporting_kpi_v2';

function v1Key(tenantId?: string | null): string {
  return `${V1_PREFIX}_${tenantId || 'global'}`;
}

function v2Key(tenantId?: string | null): string {
  return `${V2_PREFIX}_${tenantId || 'global'}`;
}

function scopeKey(scope: ReportingKpiScope): string {
  if (scope.kind === 'default') return 'default';
  return `${scope.kind}:${scope.id}`;
}

function readV1(tenantId?: string | null): ReportingKpiTargets {
  try {
    const raw = localStorage.getItem(v1Key(tenantId));
    if (!raw) return { ...DEFAULT_REPORTING_KPI_TARGETS };
    const parsed = JSON.parse(raw) as Partial<ReportingKpiTargets>;
    return { ...DEFAULT_REPORTING_KPI_TARGETS, ...parsed };
  } catch {
    return { ...DEFAULT_REPORTING_KPI_TARGETS };
  }
}

type KpiStoreV2 = {
  entries: Record<string, ReportingKpiTargets>;
  targetBasis?: ReportingKpiTargetBasis;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function normalizeTargets(raw: Partial<ReportingKpiTargets> | undefined | null): ReportingKpiTargets {
  const merged = { ...DEFAULT_REPORTING_KPI_TARGETS, ...(raw || {}) } as Record<string, unknown>;
  const out: Record<string, number> = {};
  (Object.keys(DEFAULT_REPORTING_KPI_TARGETS) as (keyof ReportingKpiTargets)[]).forEach((k) => {
    const v = merged[k as string];
    if (typeof v === 'number' && Number.isFinite(v)) out[k as string] = v;
    else if (typeof v === 'string') {
      const n = parseFloat(v.replace(',', '.'));
      out[k as string] = Number.isFinite(n) ? n : DEFAULT_REPORTING_KPI_TARGETS[k] as number;
    } else {
      out[k as string] = DEFAULT_REPORTING_KPI_TARGETS[k] as number;
    }
  });
  if ('newCompanies' in merged) {
    const v = merged.newCompanies;
    if (typeof v === 'number' && Number.isFinite(v)) out.newCompanies = v;
    else if (typeof v === 'string') {
      const n = parseFloat(v.replace(',', '.'));
      out.newCompanies = Number.isFinite(n) ? n : DEFAULT_REPORTING_KPI_TARGETS.newCompanies || 0;
    } else if (DEFAULT_REPORTING_KPI_TARGETS.newCompanies != null) {
      out.newCompanies = DEFAULT_REPORTING_KPI_TARGETS.newCompanies;
    }
  }
  return out as unknown as ReportingKpiTargets;
}

function convertTargetsBetweenBasis(
  targets: ReportingKpiTargets,
  fromBasis: ReportingKpiTargetBasis,
  toBasis: ReportingKpiTargetBasis
): ReportingKpiTargets {
  if (fromBasis === toBasis) return { ...targets };
  const factor = BASIS_DAYS[toBasis] / BASIS_DAYS[fromBasis];
  const out: ReportingKpiTargets = { ...targets };
  (Object.keys(DEFAULT_REPORTING_KPI_TARGETS) as (keyof ReportingKpiTargets)[]).forEach((key) => {
    const v = out[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      (out as Record<string, number>)[key as string] = round2(v * factor);
    }
  });
  if (out.newCompanies != null && typeof out.newCompanies === 'number' && Number.isFinite(out.newCompanies)) {
    out.newCompanies = round2(out.newCompanies * factor);
  }
  return out;
}

function normalizeBasis(b: unknown): ReportingKpiTargetBasis {
  if (b === 'day' || b === 'week' || b === 'month' || b === 'year') return b;
  return 'month';
}

function loadV2Store(tenantId: string | null | undefined): KpiStoreV2 {
  try {
    const raw = localStorage.getItem(v2Key(tenantId));
    if (raw) {
      const parsed = JSON.parse(raw) as {
        entries?: Record<string, Partial<ReportingKpiTargets>>;
        targetBasis?: ReportingKpiTargetBasis;
      };
      if (parsed?.entries && typeof parsed.entries === 'object') {
        const keys = Object.keys(parsed.entries);
        if (keys.length) {
          const entries: Record<string, ReportingKpiTargets> = {};
          for (const k of keys) {
            entries[k] = normalizeTargets(parsed.entries![k]);
          }
          return { entries, targetBasis: normalizeBasis(parsed.targetBasis) };
        }
      }
    }
  } catch {
    /* ignore */
  }
  const migrated: KpiStoreV2 = {
    entries: { default: readV1(tenantId) },
    targetBasis: 'month',
  };
  localStorage.setItem(v2Key(tenantId), JSON.stringify(migrated));
  return migrated;
}

function persistV2(tenantId: string | null | undefined, store: KpiStoreV2): void {
  localStorage.setItem(v2Key(tenantId), JSON.stringify(store));
  window.dispatchEvent(new CustomEvent('werftools-reporting-kpi-saved'));
}

/** @deprecated internal — gebruik v2 via load/save for scope */
export function getReportingKpiStorageKey(clientId?: string | null): string {
  return v1Key(clientId);
}

export function loadReportingKpiTargetsForScope(
  tenantId: string | null | undefined,
  scope: ReportingKpiScope
): ReportingKpiTargets {
  const store = loadV2Store(tenantId);
  const k = scopeKey(scope);
  const e = store.entries[k];
  return e ? normalizeTargets(e) : { ...DEFAULT_REPORTING_KPI_TARGETS };
}

export function saveReportingKpiTargetsForScope(
  tenantId: string | null | undefined,
  scope: ReportingKpiScope,
  targets: ReportingKpiTargets
): void {
  const store = loadV2Store(tenantId);
  store.entries[scopeKey(scope)] = normalizeTargets(targets);
  persistV2(tenantId, store);
}

/** Standaard-doelen voor de tenant (backward compatible met v1-key migratie). */
export function loadReportingKpiTargets(clientId?: string | null): ReportingKpiTargets {
  return loadReportingKpiTargetsForScope(clientId, { kind: 'default' });
}

export function saveReportingKpiTargets(clientId: string | null | undefined, targets: ReportingKpiTargets): void {
  saveReportingKpiTargetsForScope(clientId, { kind: 'default' }, targets);
}

/**
 * Kiest het meest specifieke beschikbare doel: bedrijf → recruiter → organisatie → tenant-default.
 */
export function resolveReportingKpiTargets(
  tenantId: string | null | undefined,
  ctx: {
    clientId?: string | null;
    recruiterId?: string | null;
    organizationId?: string | null;
  }
): ReportingKpiTargets {
  const store = loadV2Store(tenantId);
  const order: string[] = [];
  if (ctx.clientId) order.push(`client:${ctx.clientId}`);
  if (ctx.recruiterId) order.push(`recruiter:${ctx.recruiterId}`);
  if (ctx.organizationId) order.push(`organization:${ctx.organizationId}`);
  order.push('default');
  for (const key of order) {
    const e = store.entries[key];
    if (e) return { ...DEFAULT_REPORTING_KPI_TARGETS, ...e };
  }
  return { ...DEFAULT_REPORTING_KPI_TARGETS };
}

export function getReportingKpiTargetBasis(tenantId: string | null | undefined): ReportingKpiTargetBasis {
  return normalizeBasis(loadV2Store(tenantId).targetBasis);
}

export function saveReportingKpiTargetBasis(
  tenantId: string | null | undefined,
  basis: ReportingKpiTargetBasis
): void {
  const store = loadV2Store(tenantId);
  const from = normalizeBasis(store.targetBasis);
  const to = normalizeBasis(basis);
  if (from !== to) {
    const keys = Object.keys(store.entries || {});
    for (const k of keys) {
      store.entries[k] = convertTargetsBetweenBasis(normalizeTargets(store.entries[k]), from, to);
    }
  }
  store.targetBasis = to;
  persistV2(tenantId, store);
}

/**
 * Scale stored targets (defined per `basis`) to match the length of the reporting filter window.
 * @param effectiveFilterDays length of the selected period in days (for "all time", pass ~365).
 */
export function scaleReportingKpiTargetsToFilterWindow(
  targets: ReportingKpiTargets,
  basis: ReportingKpiTargetBasis,
  effectiveFilterDays: number
): ReportingKpiTargets {
  const d = Math.max(1, effectiveFilterDays);
  const scale = d / BASIS_DAYS[basis];
  const out: ReportingKpiTargets = { ...targets };
  const roundTarget = (n: number) => Math.max(0, round2(n));
  (Object.keys(DEFAULT_REPORTING_KPI_TARGETS) as (keyof ReportingKpiTargets)[]).forEach((key) => {
    const v = out[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      (out as Record<string, number>)[key as string] = roundTarget(v * scale);
    }
  });
  if (out.newCompanies != null && typeof out.newCompanies === 'number') {
    out.newCompanies = roundTarget(out.newCompanies * scale);
  }
  return out;
}
